import { Chart as ChartJS, registerables } from 'chart.js';
import { useMemo } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

import { FormatProperties } from 'models/response/modelOutput.interface';
import { CHART_COLORS } from 'projectConstants';
import {
  filterEndDate,
  filterStartDate,
  getShowNonEditableColumns,
  planPageShow,
} from 'redux/PlanPageSlice';
import { getBaseScenario as getBaseScenarioFromPlannerModeSlice } from 'redux/PlannerModeSlice';
import { getBaseScenario as getBaseScenarioFromScenarioSlice } from 'redux/ScenarioSlice';
import getChartOptions from 'utils/getChartOptions';

ChartJS.register(...registerables);

interface Properties {
  chartLabelData: { [x: string]: number };
  chartData: Record<string, Record<string, number>> | [];
  chartType: string;
  formatProperties: FormatProperties;
  showLabel?: boolean;
  dimId?: number | string;
}

const legendMargin = {
  id: 'legendMargin',
  beforeInit(chart: any) {
    const originalFit = chart.legend.fit;

    chart.legend.fit = function fit() {
      originalFit.bind(chart.legend)();
      this.height += 25;
    };
  },
};

function trimTrailingZeros(data: number[]) {
  if (data.length <= 1) return data;
  const allZeros = data.every((value) => value === 0);
  if (allZeros) return data;

  const trimmed = [...data];
  while (trimmed.length > 1 && trimmed[trimmed.length - 1] === 0) {
    trimmed.pop();
  }
  return trimmed;
}

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function toScenarioLabel(startDate?: string, looksLikeMonthly?: boolean): string | undefined {
  if (!startDate) return undefined;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return undefined;

  // Match table behavior: always use (start_date - 1 month) as anchor
  d.setMonth(d.getMonth() - 1);

  const adjustedYear = d.getFullYear();
  if (!looksLikeMonthly) return String(adjustedYear);

  const yy = String(adjustedYear).slice(-2);
  return `${monthNames[d.getMonth()]}-${yy}`;
}

function isNonEditableTimeLabel(label: string, scenarioLabel?: string) {
  if (!scenarioLabel) return false;

  // Yearly (e.g. FY24) compared against "2024"
  if (label.startsWith('FY')) {
    const scenarioYear = Number(scenarioLabel);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(label.slice(2, 4)) + 2000 < scenarioYear;
  }

  // Monthly (e.g. Nov-23) compared against "Nov-23"
  const labelParts = label.split('-');
  const scenarioParts = scenarioLabel.split('-');
  if (labelParts.length < 2 || scenarioParts.length < 2) return false;

  const labelYear = Number(labelParts[1]);
  const scenarioYear = Number(scenarioParts[1]);
  if (!Number.isFinite(labelYear) || !Number.isFinite(scenarioYear)) return false;

  if (labelYear < scenarioYear) return true;
  return (
    labelYear === scenarioYear &&
    monthNames.indexOf(labelParts[0] as typeof monthNames[number]) <=
      monthNames.indexOf(scenarioParts[0] as typeof monthNames[number])
  );
}

const IndicatorChart = ({
  chartLabelData,
  chartData,
  chartType,
  formatProperties,
  showLabel,
}: Properties) => {
  const showPlanPage = useSelector(planPageShow);
  const startDateFilter = useSelector(filterStartDate);
  const endDateFilter = useSelector(filterEndDate);
  const showNonEditableColumns = useSelector(getShowNonEditableColumns);
  const baseScenarioFromPlannerMode = useSelector(getBaseScenarioFromPlannerModeSlice);
  const baseScenarioFromScenario = useSelector(getBaseScenarioFromScenarioSlice);
  const baseScenario = baseScenarioFromPlannerMode || baseScenarioFromScenario;

  const computedData = useMemo(() => {
    if (!chartData || Object.values(chartData as any)[0] === undefined) return null;

    let labels = Object.keys(chartLabelData || {});
    let datasets = Object.entries(chartData as any).map((entry: any, index: number) => {
      const raw = Object.values(entry[1] || {}) as number[];
      return {
        key: index,
        label: entry[0],
        data: trimTrailingZeros(raw),
        backgroundColor: CHART_COLORS[index],
        borderColor: CHART_COLORS[index],
      };
    });

    const startIndex = startDateFilter ? labels.indexOf(startDateFilter) : -1;
    const endIndex = endDateFilter ? labels.indexOf(endDateFilter) : -1;

    if (startIndex !== -1 && endIndex !== -1 && endIndex >= startIndex) {
      labels = labels.slice(startIndex, endIndex + 1);
      datasets = datasets.map((ds: any) => ({
        ...ds,
        data: (ds.data || []).slice(startIndex, endIndex + 1),
      }));
    }

    if (!showNonEditableColumns) {
      const looksLikeMonthly = labels[0]?.includes('-');
      const scenarioLabel = toScenarioLabel(baseScenario?.start_date, looksLikeMonthly);

      const keepMask = labels.map((l) => !isNonEditableTimeLabel(l, scenarioLabel));
      const filteredLabels = labels.filter((_, index) => keepMask[index]);
      const filteredDatasets = datasets.map((ds: any) => ({
        ...ds,
        data: (ds.data || []).filter((_: any, index: number) => keepMask[index]),
      }));

      labels = filteredLabels;
      datasets = filteredDatasets;
    }

    return { labels, datasets };
  }, [
    chartData,
    chartLabelData,
    startDateFilter,
    endDateFilter,
    showNonEditableColumns,
    baseScenario?.start_date,
  ]);

  if (!computedData) return null;

  return (
    <ReactChart
      type={chartType === 'line' ? 'line' : 'bar'}
      data={computedData as any}
      options={getChartOptions({
        chartType,
        isLegend: !showLabel,
        formatProperties,
        pageName: 'drivers',
        aspectRatio: showPlanPage ? 1.5 : 5,
      })}
      plugins={[legendMargin]}
    />
  );
};

export default IndicatorChart;
